/**
 * Session helpers — read/write from localStorage.
 * The API client reads these keys automatically via interceptors.
 */

export const SESSION_KEYS = {
  AUTH_TOKEN: 'auth_token',
  TENANT_ID: 'tenant_id',
  USER_ID: 'user_id',
} as const;

export interface Session {
  token: string;
  tenantId: string;
  userId: string;
}

export function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, session.token);
  localStorage.setItem(SESSION_KEYS.TENANT_ID, session.tenantId);
  localStorage.setItem(SESSION_KEYS.USER_ID, session.userId);
}

export function clearSession(): void {
  Object.values(SESSION_KEYS).forEach((key) => localStorage.removeItem(key));
}

export function getSession(): Session | null {
  const token = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
  const tenantId = localStorage.getItem(SESSION_KEYS.TENANT_ID);
  const userId = localStorage.getItem(SESSION_KEYS.USER_ID);

  if (!token || !tenantId || !userId) return null;

  return { token, tenantId, userId };
}

export function isAuthenticated(): boolean {
  return getSession() !== null;
}

/**
 * Temporarily switch the active tenant context for a single async operation.
 *
 * The Axios request interceptor reads SESSION_KEYS.TENANT_ID from localStorage,
 * so swapping it before an API call and restoring it after lets the admin
 * make per-tenant requests without a full session change.
 *
 * ```ts
 * const agent = await withTenant(tenantId, () => agentService.create(payload));
 * ```
 */
export async function withTenant<T>(
  tenantId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const previous = localStorage.getItem(SESSION_KEYS.TENANT_ID);
  localStorage.setItem(SESSION_KEYS.TENANT_ID, tenantId);
  try {
    return await fn();
  } finally {
    if (previous !== null) {
      localStorage.setItem(SESSION_KEYS.TENANT_ID, previous);
    } else {
      localStorage.removeItem(SESSION_KEYS.TENANT_ID);
    }
  }
}

/** Read the current active tenant id (if any) */
export function getActiveTenantId(): string | null {
  return localStorage.getItem(SESSION_KEYS.TENANT_ID);
}

/** Persist a tenant context (useful when admin selects a working tenant) */
export function setActiveTenantId(tenantId: string): void {
  localStorage.setItem(SESSION_KEYS.TENANT_ID, tenantId);
}
